import React, { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send } from "lucide-react";

/**
 * @typedef {Object} ChatMessage
 * @property {'user'|'assistant'} role
 * @property {string} content
 */

/**
 * @typedef {Object} ChatThreadProps
 * @property {ChatMessage[]} messages
 * @property {(text: string) => void} onSend
 * @property {boolean} sending
 * @property {string} [input] - controlled input value
 * @property {(value: string) => void} [onInputChange]
 * @property {string} [placeholder]
 * @property {React.ReactNode} [emptyState] - rendered instead of the message list when messages.length === 0
 * @property {React.ReactNode} [afterMessages] - rendered after the message list, inside the scroll area (e.g. AIChatFloating's "Try asking" suggestions shown alongside its seeded greeting message)
 * @property {string} [formTestId]
 * @property {string} [inputTestId]
 * @property {string} [sendTestId]
 * @property {string} [bubbleMaxWidth] - Tailwind max-width class, defaults to "max-w-[85%]"
 * @property {string} [listClassName] - overrides message-list padding/spacing (the full-page AIChat and the AIChatFloating widget use different scale)
 * @property {string} [bubbleClassName] - overrides bubble internal padding
 */

/**
 * Shared message list + composer, extracted from AIChat.jsx (full page) and
 * AIChatFloating.jsx (widget) — both had near-identical rendering of the
 * same {role, content} message shape with duplicated JSX. The two pages'
 * "empty state" welcome UI genuinely differ (full hero vs compact
 * suggestions) and stay with each caller via the `emptyState` slot rather
 * than being forced into one shape here.
 * @param {ChatThreadProps} props
 */
export default function ChatThread({
  messages,
  onSend,
  sending,
  input,
  onInputChange,
  placeholder = "Ask anything about Indian Income Tax…",
  emptyState,
  afterMessages,
  formTestId,
  inputTestId,
  sendTestId,
  bubbleMaxWidth = "max-w-[85%]",
  listClassName = "p-4 space-y-3",
  bubbleClassName = "px-3.5 py-2.5",
}) {
  const scroller = useRef(null);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages]);

  const submit = (e) => {
    e.preventDefault();
    onSend(input);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div ref={scroller} className={`flex-1 overflow-y-auto ${listClassName}`} aria-live="polite">
        {messages.length === 0 && emptyState}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`${bubbleMaxWidth} rounded-lg ${bubbleClassName} text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
              }`}
            >
              {m.content || (sending && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
            </div>
          </div>
        ))}
        {afterMessages}
      </div>

      <form onSubmit={submit} className="border-t border-border p-3 flex items-end gap-2" data-testid={formTestId}>
        <Textarea
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          placeholder={placeholder}
          className="min-h-[44px] max-h-32 resize-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend(input);
            }
          }}
          data-testid={inputTestId}
        />
        <Button type="submit" size="icon" disabled={sending || !input.trim()} aria-label="Send message" data-testid={sendTestId}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}
